/**
 * Regroupe les libellés affichés dans l'interface pour traduire les clés
 * techniques des plans en informations immédiatement compréhensibles.
 */
export const PLAN_FEATURE_LABELS = {
  basic_whatsapp: 'Connexion WhatsApp',
  basic_orders: 'Gestion des commandes',
  whatsapp_automation: 'Automatisation WhatsApp',
  orders_management: 'Suivi des commandes',
  basic_analytics: 'Statistiques essentielles',
  delivery_tracking: 'Suivi des livraisons',
  advanced_automation: 'Automatisations avancées',
  pos_integration: 'Point de vente (POS)',
  cashier_management: 'Gestion des caissiers',
  advanced_analytics: 'Statistiques avancées',
  marketing_campaigns: 'Campagnes marketing',
  loyalty_points: 'Programme de fidélité',
  inventory_sync: 'Synchronisation des stocks',
  unlimited_all: 'Accès illimité aux quotas',
  custom_integrations: 'Intégrations sur mesure',
  api_access: 'Accès à l’API',
  white_label: 'Marque blanche',
  dedicated_support: 'Support dédié',
  custom_analytics: 'Statistiques personnalisées'
};

/**
 * Quotas affichés dans le profil et les cartes d'offre, dans l'ordre de
 * présentation. Miroir de `PLAN_LIMIT_DEFINITIONS` côté serveur ; l'API
 * `/subscription/plans` renvoie aussi cette liste dans `limits` pour rester
 * synchronisée.
 * - `period: 'month'` : quota renouvelé chaque mois (total sur l'essai gratuit).
 * - `usageKey` : champ correspondant dans la réponse `/subscription/usage-stats`.
 */
export const PLAN_LIMITS = [
  { key: 'max_messages_per_month', label: 'Messages WhatsApp', period: 'month', usageKey: 'messagesUsed' },
  { key: 'max_products', label: 'Produits actifs', period: 'total', usageKey: 'productsCount' },
  { key: 'max_delivery_persons', label: 'Livreurs', period: 'total', usageKey: 'deliveryPersonsCount' },
  { key: 'max_orders_per_month', label: 'Commandes', period: 'month', usageKey: 'ordersCount' },
  { key: 'max_zones', label: 'Zones de livraison', period: 'total', usageKey: 'zonesCount' },
  { key: 'max_users', label: 'Utilisateurs', period: 'total' },
  { key: 'max_message_templates', label: 'Modèles de message', period: 'total', usageKey: 'messageTemplatesCount' },
  { key: 'ai_points_per_month', label: 'Points IA', period: 'month', usageKey: 'aiPointsUsed' }
];

/**
 * Transforme une clé technique de fonctionnalité en texte lisible.
 * @param {string} feature - Clé enregistrée dans la configuration de l'offre.
 * @returns {string} Libellé français de la fonctionnalité.
 */
export const formatFeatureName = (feature) => {
  return PLAN_FEATURE_LABELS[feature] || feature.replace(/_/g, ' ');
};

/**
 * Affiche un quota en conservant explicitement le cas illimité.
 * @param {number|undefined} value - Valeur du quota retournée par l'API.
 * @returns {string} Valeur prête à afficher dans l'interface.
 */
export const formatPlanLimit = (value) => {
  if (value === -1) return 'Illimité';
  if (value === undefined || value === null) return 'Non défini';
  return String(value);
};

/**
 * Suffixe de période d'un quota : « / mois » pour un quota renouvelé, rien
 * sur l'essai gratuit où tous les quotas sont des totaux.
 * @param {{period: string}} limit - Définition du quota.
 * @param {boolean} isTrial - Vrai pour l'offre d'essai.
 * @returns {string} Suffixe à afficher après la valeur.
 */
export const formatLimitPeriod = (limit, isTrial = false) => {
  return limit.period === 'month' && !isTrial ? ' / mois' : '';
};

/**
 * Normalise les deux formats de quotas encore supportés par le client : le
 * format snake_case renvoyé par l'API et le format historique camelCase.
 * @param {object|undefined} plan - Configuration complète d'une offre.
 * @returns {object} Quotas dans le format utilisé par l'interface profil.
 */
export const getPlanLimits = (plan) => {
  if (plan?.limits) return plan.limits;
  return {
    max_messages_per_month: plan?.maxMessagesPerMonth,
    max_shops: plan?.maxShops,
    max_products: plan?.maxProducts,
    max_delivery_persons: plan?.maxDeliveryPersons,
    max_orders_per_month: plan?.maxOrdersPerMonth,
    max_zones: plan?.maxZones,
    max_users: plan?.maxUsers,
    max_message_templates: plan?.maxMessageTemplates,
    ai_points_per_month: plan?.aiPointsPerMonth
  };
};

/**
 * Formate un montant de plan avec sa devise, sans inventer de tarif local.
 * @param {number|undefined} price - Prix retourné par la configuration serveur.
 * @param {string} currency - Devise du plan.
 * @returns {string} Prix lisible pour l'utilisateur.
 */
export const formatPlanPrice = (price, currency = 'XOF') => {
  if (!price) return 'Gratuit';
  return `${Number(price).toLocaleString('fr-FR')} ${currency}`;
};
